import { CardUIData } from '../../types/cardDataTypes';
import { reportCardLabelMapByCategory } from '../../constants/cardLabelMap';
import {
  calculatePercentageDifference,
  getTrend,
} from '../../utils/mathUtilities';
import { getComparisonLabel } from '../../utils/dateUtilities';
import {
  avgChange,
  getValueForHeader,
  rateOfChange,
  // secondsToHumanTime,
} from './cardUtils';
import { secondsToHumanTime } from '@/utils/secondsToHumanTime';

/**
 * Metadata interface for card configuration
 */
interface CardMetadata {
  tileKey: string;
  id: string;
  componentType: 'card';
  unit?: 'time' | 'percent';
  calculationType?: 'avgChange' | 'rateOfChange';
  apiParam: 'card';
  headerKey?: string;
  metricKeys?: {
    numerator: string;
    denominator: string;
  };
  filterDateFrom?: string;
  compareDateFrom?: string;
  title?: string;
}

/**
 * Transforms API response data into a CardUIData object based on new metadata structure
 *
 * Example Metadata Formats:
 * 1. Single Metric:
 * {
 *   tileKey: 'applicants_interviewed',
 *   id: 'applicants_interviewed',
 *   componentType: 'card',
 *   headerKey: 'applicants_interviewed',
 *   unit: 'time' // optional
 * }
 *
 * 2. Combined Metrics:
 * {
 *   tileKey: 'total_interview_duration',
 *   id: 'total_interview_duration',
 *   componentType: 'card',
 *   unit: 'time', // optional
 *   calculationType: 'avgChange',
 *   metricPair: {
 *     numerator: 'total_interview_duration',
 *     denominator: 'interviews_completed'
 *   }
 * }
 */
export const cardUITransformer = ({
  response,
  metadata,
  subject_type = '',
}: {
  response: any;
  metadata: CardMetadata;
  subject_type?: string;
}): CardUIData => {
  const { headers, table = [], filter_by_date, compare_date } = response || {};
  const { headerKey, metricKeys, unit, calculationType, tileKey } = metadata;

  // Handle empty data case
  if (!table?.length) {
    const emptyComparisonLabel =
      filter_by_date?.from && compare_date?.from
        ? getComparisonLabel(filter_by_date.from, compare_date.from)
        : 'vs comparison period';

    return {
      renderType: 'card',
      header: tileKey,
      id: metadata.id,
      value: 0,
      compareValue: 'NA',
      percentageDiff: undefined,
      trend: undefined,
      comparisonLabel: `NA - ${emptyComparisonLabel}`,
      ...(reportCardLabelMapByCategory[subject_type]?.[tileKey] || {}),
    };
  }

  // Find current and comparison rows
  const dsIndex = headers.indexOf('ds');
  const [currentRow, compareRow] = table.reduce(
    (acc, row) => {
      if (row[dsIndex] === '2') acc[0] = row;
      if (row[dsIndex] === '3') acc[1] = row;
      return acc;
    },
    [undefined, undefined]
  );

  const isCompareRowPresent = Boolean(compareRow && compareRow.length > 0);

  let rawValue: number;
  let rawCompareValue: number | undefined;
  let value: number | string;
  let compareValue: number | string | undefined;

  if (headerKey) {
    // Single metric processing
    rawValue = getValueForHeader(headers, currentRow, headerKey) ?? 0;
    rawCompareValue = isCompareRowPresent
      ? getValueForHeader(headers, compareRow, headerKey)
      : undefined;
  } else if (metricKeys && calculationType) {
    // Combined metrics processing
    const calcFunc = calculationType === 'avgChange' ? avgChange : rateOfChange;

    const currentNum =
      getValueForHeader(headers, currentRow, metricKeys.numerator) ?? 0;
    const currentDen =
      getValueForHeader(headers, currentRow, metricKeys.denominator) ?? 0;

    // Special handling for rejections_time_to_reject - use raw value without division
    if (tileKey === 'rejections_time_to_reject') {
      rawValue = currentNum; // Use raw value directly without division
    } else {
      rawValue = calcFunc({
        numerator: currentNum,
        denominator: currentDen,
      });
    }

    if (isCompareRowPresent && compareRow) {
      const compareNum =
        getValueForHeader(headers, compareRow, metricKeys.numerator) ?? 0;
      const compareDen =
        getValueForHeader(headers, compareRow, metricKeys.denominator) ?? 0;

      // Special handling for rejections_time_to_reject comparison
      if (tileKey === 'rejections_time_to_reject') {
        rawCompareValue = compareNum; // Use raw value directly without division
      } else {
        rawCompareValue = calcFunc({
          numerator: compareNum,
          denominator: compareDen,
        });
      }
    }
  } else {
    console.error(
      'Invalid metadata: either headerKey or metricPair with calculationType must be provided'
    );
    return {
      renderType: 'card',
      header: tileKey,
      id: metadata.id,
      value: 'Invalid key',
    };
  }

  // Convert to time if unit is time
  if (unit === 'time') {
    value = secondsToHumanTime(rawValue);
    compareValue =
      rawCompareValue !== undefined
        ? secondsToHumanTime(rawCompareValue)
        : undefined;
  } else {
    value = rawValue;
    compareValue = rawCompareValue;
  }

  // Get comparison label from date utilities
  const comparisonLabel =
    filter_by_date?.from && compare_date?.from
      ? getComparisonLabel(filter_by_date.from, compare_date.from)
      : 'vs comparison period';

  /**
   * Data-driven comparison validation
   * 1. Basic validation: Check if we have valid numeric values
   * 2. Zero handling: Determine if zeros represent "no data" vs "actual zero value"
   * 3. Compare row validation: Ensure comparison data is meaningful
   */

  // Detect different types of data patterns
  const isZeroComparison = rawValue === 0 && rawCompareValue === 0;
  const isZeroCompareWithNonZeroCurrent =
    rawCompareValue === 0 && rawValue !== 0;

  // Check if this is likely a count/activity metric based on data patterns
  const isLikelyActivityMetric =
    // Current row has integer values (not floating point)
    Number.isInteger(rawValue) &&
    // If we have compare value, it should also be integer
    (rawCompareValue === undefined || Number.isInteger(rawCompareValue)) &&
    // Not a percentage or ratio (usually between 0 and 100 or 0 and 1)
    (rawValue > 100 || rawValue === 0);

  /**
   * Determine comparison validity based on data patterns:
   * 1. For activity/count metrics (integer values):
   *    - Zero comparison with non-zero current likely means missing data
   *    - Both zeros likely means no activity
   * 2. For ratio/percentage metrics (can have decimals):
   *    - Zero values are valid data points
   */
  const hasValidComparison =
    isCompareRowPresent &&
    typeof rawValue === 'number' &&
    typeof rawCompareValue === 'number' &&
    !isNaN(rawValue) &&
    !isNaN(rawCompareValue) &&
    !isZeroCompareWithNonZeroCurrent && // Invalid if compare is 0 but current isn't
    !(isLikelyActivityMetric && isZeroComparison); // Invalid if activity metric with no activity

  // Calculate percentage difference only for valid comparisons
  let calculatedPercentage = undefined;
  if (hasValidComparison) {
    const diff = calculatePercentageDifference(rawValue, rawCompareValue);
    calculatedPercentage = diff !== null ? diff : undefined;
    // Make percentage negative if value decreased
    if (diff !== null && rawValue < rawCompareValue) {
      calculatedPercentage = -calculatedPercentage;
    }
  }

  // Only show trend for valid comparisons with a calculated percentage
  const trend =
    hasValidComparison && calculatedPercentage !== undefined
      ? getTrend(rawValue, rawCompareValue)
      : undefined;

  // Add "NA -" prefix to comparison label for invalid comparisons
  const finalComparisonLabel = hasValidComparison
    ? comparisonLabel
    : `NA - ${comparisonLabel}`;

  return {
    renderType: 'card',
    header: tileKey,
    id: metadata.id,
    value: unit === 'percent' ? `${value}%` : value,
    compareValue: hasValidComparison ? compareValue : 'NA',
    percentageDiff: calculatedPercentage,
    trend,
    comparisonLabel: finalComparisonLabel,
    ...(reportCardLabelMapByCategory[subject_type]?.[tileKey] || {}),
    ...(metadata?.title ? { topLabel: metadata.title } : {}),
  };
};
